import { getAutoshipCadenceByValue, isFiveOrSevenWeekAutoshipCadence } from '@libs/common-types';

import type { AutoshipFrequencySelection } from '@/entities/purchase-form';

export const DEFAULT_AUTOSHIP_FREQUENCY = {
    autoshipFrequency: '4',
    autoshipUnit: '2',
} as const satisfies AutoshipFrequencySelection;

export type AutoshipFrequencySource = 'customer' | 'recommendation' | 'default';

export type ResolvedAutoshipFrequency = AutoshipFrequencySelection & {
    source: AutoshipFrequencySource;
};

export const mapRecommendedAutoshipFrequency = (
    recommendedFrequency?: string | null,
    recommendedUnit?: string | null,
    as_freq_5_7 = false
): AutoshipFrequencySelection | null => {
    if (!recommendedFrequency || !recommendedUnit) return null;

    const cadence = getAutoshipCadenceByValue(`${recommendedFrequency}_${recommendedUnit}`);
    if (!cadence) return null;
    if (!as_freq_5_7 && isFiveOrSevenWeekAutoshipCadence(cadence.value)) return null;

    return {
        autoshipFrequency: cadence.frequency,
        autoshipUnit: cadence.unitOfMeasure,
    };
};

export const resolveAutoshipFrequencySelection = ({
    isEnabled,
    customerSelection,
    recommendedFrequency,
    recommendedUnit,
    as_freq_5_7 = false,
}: {
    isEnabled: boolean;
    customerSelection?: AutoshipFrequencySelection | null;
    recommendedFrequency?: string | null;
    recommendedUnit?: string | null;
    as_freq_5_7?: boolean;
}): ResolvedAutoshipFrequency => {
    if (!isEnabled) {
        return { ...DEFAULT_AUTOSHIP_FREQUENCY, source: 'default' };
    }

    if (customerSelection) {
        const customerSelectionValue = `${customerSelection.autoshipFrequency}_${customerSelection.autoshipUnit}`;
        const isCustomerSelectionVisible = as_freq_5_7 || !isFiveOrSevenWeekAutoshipCadence(customerSelectionValue);

        if (isCustomerSelectionVisible) {
            return { ...customerSelection, source: 'customer' };
        }
    }

    const recommendation = mapRecommendedAutoshipFrequency(recommendedFrequency, recommendedUnit, as_freq_5_7);
    if (recommendation) {
        return { ...recommendation, source: 'recommendation' };
    }

    return { ...DEFAULT_AUTOSHIP_FREQUENCY, source: 'default' };
};

export const getMostCommonOptionValue = ({
    isEnabled,
    recommendedFrequency,
    recommendedUnit,
    as_freq_5_7 = false,
}: {
    isEnabled: boolean;
    recommendedFrequency?: string | null;
    recommendedUnit?: string | null;
    as_freq_5_7?: boolean;
}): string | undefined => {
    if (!isEnabled) return undefined;

    const recommendation = mapRecommendedAutoshipFrequency(recommendedFrequency, recommendedUnit, as_freq_5_7);
    if (!recommendation) return undefined;

    return `${recommendation.autoshipFrequency}_${recommendation.autoshipUnit}`;
};
